import { z } from 'zod';

/**
 * **The four fields a person is, as one schema** — §6.4's form and §6.5's *Edit* are the same four
 * fields, so they are described once (`.claude/rules/frontend.md` → Forms: the zod schema **is** the
 * form type).
 *
 * §6.4 fixes their shape and the reasons are its own:
 *
 *  * `full_name` **required** — the only field the product cannot render a candidate without;
 *  * `email` and `phone` **both optional**, because a recruiter often has only one of them, and a
 *    blank key matches nothing at all in the duplicate check (ADR 0014) rather than matching
 *    everything;
 *  * `source` **free text, deliberately not a fixed set** — *"Referral (Tom)"* and *"AngelList, via
 *    the CTO"* are what people actually write, and a `Select` of five would turn every one of them
 *    into *"Other"*.
 *
 * Every value is trimmed **by the schema**, so the body sent and the keys matched are the same
 * strings the person typing them can see they typed.
 *
 * ⚠️ **What is *not* here is the vacancy** (§6.4 adds it by `extend`): a vacancy belongs to a
 * consideration, never to a person (`PersonUpdate` carries no `role_id` — *"a status or a vacancy on
 * a person is ADR 0002 running backwards"*), so §6.5's *Edit* cannot offer it and §6.4's creation
 * must.
 */
export const personFieldsSchema = z.object({
  full_name: z.string().trim().min(1),
  email: z.string().trim().email().or(z.literal('')),
  phone: z.string().trim(),
  source: z.string().trim(),
});

export type PersonFieldsForm = z.infer<typeof personFieldsSchema>;

/**
 * The field names, **derived from the schema** rather than listed beside it — a field added above is
 * a field §6.5's *Edit* diffs and §6.4's dialog renders, with nothing to keep in step by hand.
 */
export const PERSON_FIELDS = Object.keys(personFieldsSchema.shape) as Array<keyof PersonFieldsForm>;
